import { Button } from "@design-system/components";
import { t } from "@lingui/core/macro";
import { useImpersonation } from "@spiriidk/auth";
import React from "react";

function buildImpersonationLabel(
  company: { name: string } | null,
  operator: { name: string } | null
): React.ReactNode {
  if (company && operator) {
    return (
      <>
        {t`Impersonating customer`}{" "}
        <span className="font-semibold">{company.name}</span> {t`at operator`}{" "}
        <span className="font-semibold">{operator.name}</span>
      </>
    );
  }

  if (company) {
    return (
      <>
        {t`Impersonating customer`}{" "}
        <span className="font-semibold">{company.name}</span>
      </>
    );
  }

  return (
    <>
      {t`Impersonating operator`}{" "}
      <span className="font-semibold">{operator!.name}</span>
    </>
  );
}

interface ImpersonatedViewFrameProps {
  onEditClick: () => void;
}

function ImpersonatedViewFrame({ onEditClick }: ImpersonatedViewFrameProps) {
  const { isImpersonating, operator, company, clearImpersonation } =
    useImpersonation();

  if (!isImpersonating) {
    return null;
  }

  return (
    <div
      className="border-shape-warning-filled-background pointer-events-none fixed inset-0 z-[100] flex flex-col justify-end rounded-lg border-4"
      role="status"
      aria-live="polite"
      aria-label={t`Impersonation active`}
    >
      <div className="flex justify-center">
        <div className="bg-shape-warning-filled-background pointer-events-auto relative flex items-center gap-3 rounded-t-lg px-3 py-2">
          <p className="text-shape-warning-filled-foreground-default text-xs">
            {buildImpersonationLabel(company, operator)}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="default" size="smaller" onClick={onEditClick}>
              {t`Edit`}
            </Button>
            <Button
              variant="default"
              size="smaller"
              onClick={clearImpersonation}
            >
              {t`Stop`}
            </Button>
          </div>
          {/* Concave fillets that round the join where the badge meets the bottom border */}
          <span
            aria-hidden
            className="bg-shape-warning-filled-background pointer-events-none absolute right-full bottom-0 size-2 [mask-image:radial-gradient(circle_at_top_left,transparent_8px,#000_8px)]"
          />
          <span
            aria-hidden
            className="bg-shape-warning-filled-background pointer-events-none absolute bottom-0 left-full size-2 [mask-image:radial-gradient(circle_at_top_right,transparent_8px,#000_8px)]"
          />
        </div>
      </div>
    </div>
  );
}

export { ImpersonatedViewFrame };
export type { ImpersonatedViewFrameProps };
